/* samplejob — ONE priced sample job, used by every page that shows a product's
   price before a real estimate exists.

   There were three different previews on three pages, each computed a
   different way:

     · the product editor:  material $/ft × markup            (~$20.35/ft)
     · the products catalog: computeQuote({ linearFeet: 100 }) (~$26.19/ft)
     · settings:            the markup formula in prose, no number at all

   None of them was wrong arithmetic; they were answers to different questions,
   presented as if they were the same one. So a contractor set a price in the
   editor and met a different number in the catalog.

   Everything now runs the real engine — the same computeQuote() that prices a
   customer's estimate — over a job whose assumptions are stated next to the
   answer rather than hidden in a tooltip.

   Markup and margin are NOT synonyms and are never printed as if they were:
   2× markup is 50% margin; the object carries both. */

export type SampleInputs = { linearFeet: number; gates: number; height?: number; corners?: number };

export const SAMPLE_DEFAULTS: SampleInputs = { linearFeet: 150, gates: 1, corners: 0 };

const round2 = (n: any) => Math.round((Number(n) || 0) * 100) / 100;

export type SampleJob = {
  ok: boolean;
  inputs: SampleInputs;
  /** what the contractor pays */
  materials: number;
  labor: number;
  laborHours: number;
  /** what the markup adds on top of cost, as money */
  markupAmount: number;
  markup: number;          // the multiplier, e.g. 2
  marginPct: number;       // the resulting margin, e.g. 50
  addons: number;          // customer-facing add-ons (gates, permit, travel…)
  slope: number;
  subtotal: number;
  tax: number;
  taxRate: number;
  taxLabel: string;
  customerTotal: number;
  perFoot: number;
  /** one line stating the assumptions, for display beside the number */
  assumption: string;
  raw: any;
};

/** price `product` for a sample job with the business's real settings */
export function sampleJob(product: any, settings: any, inputs?: Partial<SampleInputs>): SampleJob {
  const i: SampleInputs = { ...SAMPLE_DEFAULTS, ...(inputs || {}) };
  const blank: SampleJob = {
    ok: false, inputs: i, materials: 0, labor: 0, laborHours: 0, markupAmount: 0,
    markup: 0, marginPct: 0, addons: 0, slope: 0, subtotal: 0, tax: 0, taxRate: 0,
    taxLabel: "Sales tax", customerTotal: 0, perFoot: 0, assumption: assumptionLine(i), raw: null,
  };
  if (!product || !(i.linearFeet > 0)) return blank;
  let q: any = null;
  try {
    q = (window as any).VFP.computeQuote(
      { linearFeet: i.linearFeet, gates: i.gates, corners: i.corners || 0, height: i.height },
      product, settings || {},
    );
  } catch (e) { return blank; }
  if (!q) return blank;

  const materials = round2(q.materialTotal);
  const labor = round2(q.laborCost);
  const base = round2(q.base);
  const addons = round2((q.addons || []).filter((a: any) => a.customerFacing).reduce((t: number, a: any) => t + (Number(a.amount) || 0), 0));
  return {
    ok: true, inputs: i,
    materials, labor, laborHours: round2(q.laborHours),
    // what the markup itself contributes: the fence line minus the cost inside it
    markupAmount: round2(base - materials - labor),
    markup: Number(q.markup) || 0,
    marginPct: Number(q.marginPct) || 0,
    addons, slope: round2(q.slopeAmount),
    subtotal: round2(q.subtotal), tax: round2(q.tax),
    taxRate: Number(q.taxRate) || 0, taxLabel: q.taxLabel || "Sales tax",
    customerTotal: round2(q.customerTotal),
    perFoot: i.linearFeet > 0 ? round2(q.customerTotal / i.linearFeet) : 0,
    assumption: assumptionLine(i),
    raw: q,
  };
}

export function assumptionLine(i: SampleInputs) {
  return `${i.linearFeet} ft · ${i.gates} gate${i.gates === 1 ? "" : "s"}${i.height ? ` · ${i.height} ft high` : ""} · your crew rate, markup and tax`;
}

/** 2× markup is 50% margin. Said once, here, so no page has to guess. */
export function markupToMargin(markup: number) {
  const m = Number(markup) || 0;
  return m > 0 ? (1 - 1 / m) * 100 : 0;
}
